import type { Logger } from '../types/logger';
import {
  makeValidationFunction,
  validateArrayProperty,
  validateObjectProperty,
  validateOptionalArrayProperty,
  validateOptionalObjectProperty,
  validateOptionalStringProperty,
  validateOrThrow,
  validateStringOrNullProperty,
  validateStringProperty,
  validateOptionalBooleanProperty,
} from '../util/validation';
import type { FilterChoice, FilterableOutputProfile } from '../transcoding/filterableOutputProfile';
import { COMPACT_BROADCAST_FILTER_DEFINITIONS, COMPACT_GENERIC_FILTER_DEFINITIONS, filterProfiles } from '../transcoding/filterableOutputProfile';
import { generateWorkingBroadcastProfiles } from '../transcoding/broadcastProfiles';
import { generateWorkingGenericProfiles } from '../transcoding/genericOutputProfiles';
import type { RenderStrategy } from '../transcoding/renderTypes';
import isArray from 'lodash/isArray';
import type { Timecode } from '../util/timecode';
import { validateTimecode } from '../util/timecode';
import type { TimecodeRange } from '../transcoding/mxf/timecodeRewrite';
import { validateAudioMapping } from './clipListDto';
import type { OutputProfile, VideoOutputProfile } from '../transcoding/outputProfile';
import { validateOutputProfile } from '../transcoding/outputProfile';

export type PresetCapability = 'rendering' | 'direct_s3_live' | 'web_proxy' | 'edit_proxy' | 'transparency' | 'growing' | 'audio-only' | 'highres_conversion';

/**
 *  Emergent capabilities includes capabilities that are not directly selected
 *  but emerges based on the selection of others. For instance:
 *
 *    not 'audio-only' implies 'video'.
 */
export type EmergentPresetCapability = PresetCapability | 'video';

export interface EncodingPresetCommonParameters {
  /**
   * Name of the preset.
   */
  name: string,

  /**
   * Different applications where the preset can be used.
   */
  capabilities: PresetCapability[],

  /**
   * If set to undefined it is visible to everyone, otherwise
   * it is a list of groups that this encoding preset shall be
   * visible for. Note that the encoding preset will always be
   * visible in the preset manager regardless of visibility
   * rules.
   */
  visibleTo: string[] | undefined,

  /**
   * If set, override the timecode of the output. For transcoding
   * this will only set the start timecode. For MXF rewrap the start
   * timecode in the header will be overridden and also the per frame
   * timecodes.
   */
  startTimecode: Timecode | undefined,

  /**
   * Audio mix matrix for this preset. If not specified it is default a
   * diagonal matrix.
   */
  audioMapping: number[][] | undefined,

  /**
   * Custom labels for input audio tracks. Array index corresponds to track index.
   * If undefined or empty, default labels "A1", "A2", etc. are used.
   */
  audioTrackLabels: AudioTrackLabel[] | undefined,

  /**
   * When true, closed captions from the main track of source clips are
   * embedded into the MXF output. Only relevant for MXF container presets.
   * undefined for older presets that pre-date this field.
   */
  embedClosedCaptions: boolean | undefined,

  /**
   * Free-form string tags for matching this preset with graphics templates.
   */
  tags: string[] | undefined,
}

/**
 * Custom label for an audio track in a preset.
 * Empty strings indicate no label/description is set.
 */
export interface AudioTrackLabel {
  /** Short label displayed in timeline (max 10 characters). Empty string = use default. */
  label: string;
  /** Optional longer description shown as tooltip. Empty string = no description. */
  description: string;
}

export interface UnresolvedPresetFilters {
  /**
   * This maps to the filter 'mode' selected by the user in the preset dialog. 'broadcast' mode lets
   * the user choose between pre-defined sets of filters specific to different broadcast formats.
   * 'generic' mode lets the user choose general encoding parameters like container format, codec etc.
   */
  type: 'generic' | 'broadcast',
  choices: FilterChoice[],
}

/**
 * A preset that contains label-value pairs of filters chosen by the user. They need to be resolved
 * to the actual output profile by inspecting the chosen filters.
 */
export interface UnresolvedPreset extends EncodingPresetCommonParameters {
  type: 'unresolved',
  filters: UnresolvedPresetFilters,
}

/**
 * A preset that indicates that the container should be preserved at byte level. This preset allow
 * specifying a fallback presetId that will be used if the operation for whatever reason can't be
 * performed using rewrap.
 */
export interface RewrapPreset extends EncodingPresetCommonParameters {
  type: 'rewrap',
  fallbackPresetId: string | null,
}

/**
 * A preset that includes a resolve output profile.
 */
export interface ResolvedPreset extends EncodingPresetCommonParameters {
  type: 'resolved',
  outputProfile: OutputProfile,
}

export type EncodingPresetParameters = UnresolvedPreset | RewrapPreset | ResolvedPreset;

export type EncodingPreset = {
  id: string,
  createdByUserId: string,
} & EncodingPresetParameters;

export type PostEncodingPresetDto = {
  id: string,
} & EncodingPresetParameters;

export type PutEncodingPresetDto = EncodingPresetParameters;

export interface EncodingPresetUsageDtoGeneral {
  type: 'tenant-config-subclip'
}

export interface EncodingPresetUsageDtoLiveSource {
  type: 'live-source',
  liveSourceId: string,
  liveSourceName: string,
}

export interface EncodingPresetUsageDtoKeldaStorage {
  type: 'kelda-storage',
  keldaId: string,
  keldaName: string,
  storageFolderId: string,
  storageFolderTitle: string,
}

export type EncodingPresetUsageDto =
  EncodingPresetUsageDtoGeneral |
  EncodingPresetUsageDtoKeldaStorage |
  EncodingPresetUsageDtoLiveSource;

export type GetEncodingPresetDto = EncodingPreset & {
  usage: EncodingPresetUsageDto[],
};

export function satisfiesCapabilities(requiredCapabilities: EmergentPresetCapability[], available: PresetCapability[]): boolean {
  for (const requiredCapability of requiredCapabilities) {
    switch (requiredCapability) {
      case 'video': {
        if (available.includes('audio-only')) {
          return false;
        }
        break;
      }
      default: {
        if (!available.includes(requiredCapability)) {
          return false;
        }
      }
    }
  }
  return true;
}

const validateChoices =
  makeValidationFunction<FilterChoice>((u) => {
    return {
      label: validateStringProperty(u, 'label'),
      value: validateStringProperty(u, 'value'),
    };
  });

const validateFilters =
  makeValidationFunction<UnresolvedPresetFilters>((u) => {
    const type = validateStringProperty(u, 'type');
    if (type !== 'generic' && type !== 'broadcast') {
      throw `Invalid filters type: ${type}. Must be either 'generic' or 'broadcast'`;
    }
    return {
      type,
      choices: validateArrayProperty(u, 'choices', 'UnresolvedFilterChoice', validateChoices),
    };
  });

const validateCapabilities =
  makeValidationFunction<PresetCapability>((u) => {
    if (typeof u !== 'string') {
      throw 'not a string';
    }
    switch (u) {
      case 'rendering':
      case 'direct_s3_live':
      case 'web_proxy':
      case 'edit_proxy':
      case 'transparency':
      case 'growing':
      case 'audio-only':
      case 'highres_conversion':
        return u;
      default:
        throw `"${u}" is no a valid preset capability`;
    }
  });


const validateVisibleToList =
  makeValidationFunction<string[]>((u) => {
    if (!isArray(u)) {
      throw 'Not an array';
    }
    const groups : string[] = [];
    for (const e of u) {
      if (typeof e !== 'string') {
        throw 'One or more elements in the array is not a string';
      }
      groups.push(e);
    }
    return groups;
  });

const validateAudioTrackLabel =
  makeValidationFunction<AudioTrackLabel>((u) => {
    return {
      label: validateStringProperty(u, 'label'),
      description: validateOptionalStringProperty(u, 'description') ?? '',
    };
  });

const validateStringTag =
  makeValidationFunction<string>((u) => {
    if (typeof u !== 'string') {
      throw 'Not a string';
    }
    if (u === '') {
      throw 'Tag must not be empty';
    }
    return u;
  });

export const validateEncodingPresetParameters =
  makeValidationFunction<EncodingPresetParameters>((u) => {
    const common : EncodingPresetCommonParameters = {
      name: validateStringProperty(u, 'name'),
      capabilities: validateArrayProperty<PresetCapability>(u, 'capabilities', 'PresetCapability', validateCapabilities),
      visibleTo: validateOptionalObjectProperty(u, 'visibleTo', 'VisibleToList', validateVisibleToList),
      startTimecode: validateOptionalObjectProperty(u, 'startTimecode', 'Timecode', validateTimecode),
      audioMapping: validateOptionalObjectProperty(u, 'audioMapping', 'Matrix', validateAudioMapping),
      audioTrackLabels: validateOptionalArrayProperty(u, 'audioTrackLabels', 'AudioTrackLabel', validateAudioTrackLabel),
      embedClosedCaptions: validateOptionalBooleanProperty(u, 'embedClosedCaptions'),
      tags: validateOptionalArrayProperty(u, 'tags', 'string', validateStringTag),
    };
    const type = validateStringProperty(u, 'type');
    if (type === 'unresolved') {
      return {
        type,
        filters: validateObjectProperty(u, 'filters', 'UnresolvedFilters', validateFilters),
        ...common,
      };
    }
    if (type === 'rewrap') {
      return {
        type,
        fallbackPresetId: validateStringOrNullProperty(u, 'fallbackPresetId'),
        ...common,
      };
    }
    if (type === 'resolved') {
      return {
        type,
        outputProfile: validateObjectProperty(u, 'outputProfile', 'OutputProfile', validateOutputProfile),
        ...common,
      };
    }

    throw `Unknown preset type: ${type}`;
  });

export const validatePostEncodingPresetDto =
  makeValidationFunction<PostEncodingPresetDto>((u) => {
    return {
      id: validateStringProperty(u, 'id'),
      ...validateOrThrow(u, validateEncodingPresetParameters),
    };
  });

export const validatePutEncodingPresetDto =
  makeValidationFunction<PutEncodingPresetDto>((u) => {
    return {
      ...validateOrThrow(u, validateEncodingPresetParameters),
    };
  });

function addDefaultRenderingColorSpace(videoProfile: VideoOutputProfile | undefined): VideoOutputProfile | undefined {
  if (videoProfile === undefined) {
    return undefined;
  }

  if (videoProfile.colorSpace !== undefined) {
    return videoProfile;
  }

  return {
    ...videoProfile,
    colorSpace: { space: 'bt709', range: 'full' }
  };
}

function repairLegacyOutputProfile(
  capabilities: PresetCapability[],
  outputProfile: OutputProfile,
): OutputProfile {
  /**
   *  First generation of advanced presets did not specify output color space
   *  meaning that the transcoding graph system treats it as "Same as source".
   *  This will result in the sRGB intermediate format being used in the file
   *  being written. In order to avoid this for advanced presets already
   *  created by customers we apply the default color space here.
   */
  if (capabilities.includes('rendering')) {
    outputProfile = {
      container: outputProfile.container,
      video: addDefaultRenderingColorSpace(outputProfile.video),
      audio: outputProfile.audio,
    };
  }
  return outputProfile
}

export function resolveEncodingPreset(logger: Logger, preset: EncodingPresetParameters): FilterableOutputProfile | null {
  if (preset.type === 'rewrap') {
    return null;
  }

  if (preset.type === 'resolved') {
    return {
      type: 'generic',
      ...repairLegacyOutputProfile(preset.capabilities, preset.outputProfile),
    };
  }

  const { profiles, filters } =
    (preset.filters.type === 'broadcast') ?
      { profiles: generateWorkingBroadcastProfiles(), filters: COMPACT_BROADCAST_FILTER_DEFINITIONS } :
      { profiles: generateWorkingGenericProfiles(), filters: COMPACT_GENERIC_FILTER_DEFINITIONS };

  const matches = filterProfiles(filters, profiles, preset.filters.choices);
  if (matches.length === 0) {
    return null;
  }
  if (matches.length > 1) {
    logger.warn(`Encoding preset matches ${matches.length} profiles. Using the first profile.'`);
  }
  return matches[0]!;
}

export function getRenderStrategyFromEncodingPreset(
  logger: Logger,
  presetId: string,
  presets: EncodingPreset[],
  rewrapPossible: boolean,
): RenderStrategy | null {
  const encodingPreset = getPresetWithFallback(logger, presets, presetId, rewrapPossible);
  if (encodingPreset === null) {
    logger.error(`Preset with id "${presetId}" does not exist!`);
    return null;
  }

  if (encodingPreset.type === 'rewrap') {
    if (rewrapPossible === false) {
      logger.error(`Rewrap preset "${encodingPreset.id}" requested, but rewrap is not possible.`);
      return null;
    }
    const timecodeRanges : TimecodeRange[] = [];
    if (encodingPreset.startTimecode !== undefined) {
      timecodeRanges.push({
        firstFrame: 0,
        firstTimecode: encodingPreset.startTimecode,
        perFrameDelta: 1
      });
    }
    return { type: 'mxf_rewrap', timecodeRanges };
  }
  const profile = resolveEncodingPreset(logger, encodingPreset);
  if (profile === null) {
    logger.error(`Unresolved transcoding preset ${encodingPreset.id} dit not resolve to a profile`);
    return null;
  }

  return {
    type: 'transcode',
    outputProfile: profile,
    startTimecode: encodingPreset.startTimecode,
    audioMapping: encodingPreset.audioMapping,
  };
}



/**
 *  Will look up and return the preset with the given presetId from the
 *  provided list of presets. If fallback preset is needed the fallback
 *  preset will be returned instead. The fallback lookup is recursive.
 */
export function getPresetWithFallback(
  logger: Logger | undefined,
  presets: EncodingPreset[],
  presetId: string,
  rewrapPossible: boolean,
): EncodingPreset | null {
  const preset = presets.find((p) => p.id === presetId);
  if (preset === undefined) {
    return null;
  }

  if ((preset.type === 'rewrap') && !rewrapPossible) {
    if (preset.fallbackPresetId) {
      logger?.info(`Re-wrap preset "${presetId}" requested, but rewrap is not possible for the given operation. Using fallback preset "${preset.fallbackPresetId}" instead`);
      return getPresetWithFallback(logger, presets, preset.fallbackPresetId, rewrapPossible);
    }
  }

  return preset;
}


/**
 *  Recursively walk all the preset references and populate the references
 *  set with the id of all presets referenced from the input presetId.
 */
export function resolveReferencedPresetsForPreset(
  presetId: string,
  presets: EncodingPreset[],
  references: Set<string>
) {
  if (references.has(presetId)) {
    return;
  }
  references.add(presetId);

  const preset = presets.find((p) => p.id === presetId);
  if (preset === undefined) {
    return;
  }
  if (preset.type === 'rewrap' && preset.fallbackPresetId) {
    resolveReferencedPresetsForPreset(preset.fallbackPresetId, presets, references);
  }
}


export function encodingPresetIsVisible(groups: string[], encodingPreset: EncodingPreset): boolean {
  const { visibleTo } = encodingPreset;
  if (visibleTo === undefined) {
    return true;
  }
  return groups.some((g) => visibleTo.includes(g));
}

export function humanReadablePresetCapability(presetCapability: PresetCapability): string {
  switch (presetCapability) {
    case 'rendering':
      return 'Rendering';
    case 'direct_s3_live':
      return 'S3 segments';
    case 'web_proxy':
      return 'Web proxy';
    case 'edit_proxy':
      return 'Edit proxy';
    case 'transparency':
      return 'Transparency';
    case 'growing':
      return 'Growing';
    case 'audio-only':
      return 'Audio-only';
    case 'highres_conversion':
      return 'Highres conversion';
  }
}

